/**
 * Webhook ve API anahtarı sözleşmeleri (M5 WP 5.5; K-M5-03/04/05/50; migration 0065). Teamgram'da bu ekranlar plan kapılı
 * olduğundan keşifte erişilemedi (O41/V37 "API anahtarı" sekmesi, V56 "Web kancaları") — aşağıdaki davranış ÜK/PM kararıdır,
 * Teamgram kanıtı değildir (docs/planning/m5-d5-notlar.md). Belge: docs/spec/castinflow/webhooks-api.md.
 *
 * API anahtarı: kullanıcıya aittir ve yalnız o kullanıcının yetkisinin kapsamla kesişimiyle çalışır; değer yalnız oluşturma
 * yanıtında bir kez döner, sunucuda yalnız özet saklanır. Kapsam = kaynak (kayıt tipi) × erişim (`read` | `write`; yazma
 * okumayı içerir). Anahtar yalnız kayıt API uçlarında (`X-Api-Token` başlığı) kabul edilir.
 *
 * Webhook: tenant aboneliği (URL, olay türleri, kayıt tipi süzgeci). Her teslim olay kimliği + sürüm + zaman damgası + HMAC-SHA256
 * imzası taşır (`Castinflow-Signature: t=<unix>,v1=<hex>`, imza = HMAC(sır, `${t}.${gövde}`)). Olay yükü İNCEDİR: yalnız kayıt
 * kimliği, tipi ve numarası; alan değeri taşımaz (alıcı ayrıntıyı API anahtarıyla, ACL altında okur).
 */
import { z } from 'zod';
import { M2_CORE_RECORD_TYPES } from './records.js';
const uuid = z.guid();
const isoDate = z.string();
// ---------------------------------------------------------------------------------------------------------------------
// API anahtarları
// ---------------------------------------------------------------------------------------------------------------------
/** Kapsamlanabilen kaynaklar: kayıt çerçevesinin tipleri (PM D5-03) */
export const API_TOKEN_RESOURCES = M2_CORE_RECORD_TYPES;
export const API_TOKEN_ACCESS = ['read', 'write'];
/** HTTP başlığı (küçük harf; Express `req.header` büyük/küçük harf duyarsızdır) */
export const API_TOKEN_HEADER = 'x-api-token';
/**
 * Oturum katmanı 401 işareti (M6 WP 6.3). Kimlik doğrulama ara katmanı, çerez/Bearer oturumu yok ya da geçersizken verdiği
 * 401 yanıtına bu başlığı `required` değeriyle ekler. Web istemcisi YALNIZ bu işaretli 401'de oturumu sessizce yeniler ve
 * isteği yeniden gönderir. İşaretsiz 401 (yeniden kimlik doğrulamada yanlış parola / 2FA kodu) bir İŞ yanıtıdır: yenileme
 * ve yeniden gönderim yapılmaz — aksi hâlde tek yanlış parola iki deneme sayılır ve oturum gereksiz yere döndürülür.
 */
export const SESSION_AUTH_HEADER = 'x-session-auth';
export const SESSION_AUTH_REQUIRED = 'required';
/** Kullanıcı başına etkin (iptal edilmemiş, süresi dolmamış) anahtar tavanı */
export const API_TOKEN_MAX_ACTIVE_PER_USER = 10;
export const API_TOKEN_MAX_DAYS = 365;
export const API_TOKEN_DEFAULT_DAYS = 90;
export const API_TOKEN_DEFAULT_RATE = 60;
export const API_TOKEN_MAX_RATE = 600;
export const apiTokenScopeSchema = z
    .object({ resource: z.enum(API_TOKEN_RESOURCES), access: z.enum(API_TOKEN_ACCESS) })
    .strict();
export const createApiTokenInputSchema = z
    .object({
    name: z.string().trim().min(1).max(80),
    scopes: z
        .array(apiTokenScopeSchema)
        .min(1)
        .max(API_TOKEN_RESOURCES.length)
        .refine((s) => new Set(s.map((x) => x.resource)).size === s.length, {
        message: 'Her kaynak bir kez seçilir',
    }),
    expiresInDays: z.number().int().min(1).max(API_TOKEN_MAX_DAYS).default(API_TOKEN_DEFAULT_DAYS),
    rateLimitPerMinute: z
        .number()
        .int()
        .min(1)
        .max(API_TOKEN_MAX_RATE)
        .default(API_TOKEN_DEFAULT_RATE),
})
    .strict();
export const API_TOKEN_STATES = ['active', 'expired', 'revoked'];
export const apiTokenViewSchema = z.object({
    id: uuid,
    userId: uuid,
    name: z.string(),
    /** Değerin son 4 karakteri (tanıma için; değer bir daha gösterilmez) */
    hint: z.string(),
    scopes: z.array(apiTokenScopeSchema),
    rateLimitPerMinute: z.number().int(),
    expiresAt: isoDate,
    revokedAt: isoDate.nullable(),
    revokeReason: z.enum(['owner', 'admin']).nullable(),
    lastUsedAt: isoDate.nullable(),
    createdAt: isoDate,
    state: z.enum(API_TOKEN_STATES),
});
export const apiTokenListSchema = z.object({
    tokens: z.array(apiTokenViewSchema),
    /** Görüntüleyen bu kullanıcıya anahtar oluşturabilir mi (yalnız kendisi) */
    canCreate: z.boolean(),
});
/** Oluşturma yanıtı: `secret` YALNIZ burada, bir kez döner */
export const createdApiTokenSchema = z.object({ token: apiTokenViewSchema, secret: z.string() });
// ---------------------------------------------------------------------------------------------------------------------
// Webhook'lar
// ---------------------------------------------------------------------------------------------------------------------
/** Aboneliğe açık olay türleri (outbox `domain_events` adları; PM D5-10) */
export const WEBHOOK_EVENT_TYPES = [
    'record.created',
    'record.updated',
    'record.deleted',
    'record.restored',
];
/** Elle "Deneme gönder" olayı (abonelik süzgecinden bağımsız) */
export const WEBHOOK_PING_EVENT = 'webhook.ping';
export const WEBHOOK_RECORD_TYPES = M2_CORE_RECORD_TYPES;
export const WEBHOOK_PAYLOAD_VERSION = 1;
export const WEBHOOK_SIGNATURE_HEADER = 'castinflow-signature';
/** Tenant başına abonelik tavanı */
export const WEBHOOK_MAX_SUBSCRIPTIONS = 20;
/** Bir mantıksal teslimin otomatik deneme sayısı (ilk deneme dahil) */
export const WEBHOOK_MAX_ATTEMPTS = 5;
/**
 * İmza sırrı rotasyonunda geçiş süresi (M6-H3): bu süre boyunca her teslim YENİ ve ÖNCEKİ sırla iki imza (`v1=…,v1=…`)
 * taşır; alıcı bildiği sırla doğrular. Süre dolunca önceki sır imha edilir. 0 = geçişsiz (önceki sır hemen geçersiz).
 */
export const WEBHOOK_ROTATION_DEFAULT_OVERLAP_HOURS = 24;
export const WEBHOOK_ROTATION_MAX_OVERLAP_HOURS = 168;
/** Arayüzün sunduğu seçenekler (saat); API 0…`WEBHOOK_ROTATION_MAX_OVERLAP_HOURS` arasındaki her tam saati kabul eder */
export const WEBHOOK_ROTATION_OVERLAP_CHOICES = [0, 1, 24, 72, 168];
export const webhookRotateInputSchema = z
    .object({
    overlapHours: z
        .number()
        .int()
        .min(0)
        .max(WEBHOOK_ROTATION_MAX_OVERLAP_HOURS)
        .default(WEBHOOK_ROTATION_DEFAULT_OVERLAP_HOURS),
})
    .strict();
/** Geçiş süresindeki önceki sır: yalnız sürüm ve bitiş zamanı (değer hiçbir yanıtta yoktur) */
export const webhookPreviousSecretSchema = z.object({
    version: z.number().int(),
    /** Bu zamana kadar teslimler önceki sırla DA imzalanır; sonra önceki sır imha edilir */
    expiresAt: isoDate,
});
export const WEBHOOK_EVENT_LABELS = {
    'record.created': 'Kayıt oluşturuldu',
    'record.updated': 'Kayıt güncellendi',
    'record.deleted': 'Kayıt silindi',
    'record.restored': 'Kayıt geri alındı',
};
/** Yalnız http(s), kullanıcı bilgisi/parça yok; ağ denetimi (iç adres, port) sunucuda ayrıca yapılır */
export const webhookUrlSchema = z
    .string()
    .trim()
    .min(10)
    .max(2048)
    .refine((v) => {
    try {
        const u = new URL(v);
        return ((u.protocol === 'https:' || u.protocol === 'http:') &&
            u.username === '' &&
            u.password === '' &&
            u.hash === '' &&
            u.hostname.length > 0);
    }
    catch {
        return false;
    }
}, { message: 'Geçerli bir http(s) adresi girin (kullanıcı bilgisi ve # parçası olmadan)' });
export const webhookSubscriptionInputSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    url: webhookUrlSchema,
    eventTypes: z
        .array(z.enum(WEBHOOK_EVENT_TYPES))
        .min(1)
        .max(WEBHOOK_EVENT_TYPES.length)
        .refine((a) => new Set(a).size === a.length, { message: 'Olay türü tekrarlanamaz' }),
    /** Boş = tüm kayıt tipleri */
    recordTypes: z
        .array(z.enum(WEBHOOK_RECORD_TYPES))
        .max(WEBHOOK_RECORD_TYPES.length)
        .refine((a) => new Set(a).size === a.length, { message: 'Kayıt tipi tekrarlanamaz' })
        .default([]),
    isActive: z.boolean().default(true),
})
    .strict();
export const webhookSubscriptionViewSchema = z.object({
    id: uuid,
    name: z.string(),
    url: z.string(),
    eventTypes: z.array(z.enum(WEBHOOK_EVENT_TYPES)),
    recordTypes: z.array(z.enum(WEBHOOK_RECORD_TYPES)),
    /** Olay yükü bu kullanıcının görebildiği kayıtlarla sınırlıdır (oluşturan yönetici) */
    ownerId: uuid,
    ownerName: z.string(),
    isActive: z.boolean(),
    secretVersion: z.number().int(),
    secretRotatedAt: isoDate.nullable(),
    /** Rotasyon geçişi sürüyorsa önceki sır (sürüm + bitiş); yoksa null */
    previousSecret: webhookPreviousSecretSchema.nullable(),
    createdAt: isoDate,
    updatedAt: isoDate,
    /** Son 7 gün teslim özetleri */
    stats: z.object({
        delivered: z.number().int(),
        failed: z.number().int(),
        unknown: z.number().int(),
    }),
});
export const webhookSubscriptionListSchema = z.object({
    subscriptions: z.array(webhookSubscriptionViewSchema),
    /** Otomasyon "Kanca gönder" URL hedefleri için tenant imza sırrı tanımlı mı */
    tenantSecret: z.object({
        configured: z.boolean(),
        version: z.number().int().nullable(),
        rotatedAt: isoDate.nullable(),
        /** Rotasyon geçişi sürüyorsa önceki sır (sürüm + bitiş); yoksa null */
        previous: webhookPreviousSecretSchema.nullable(),
    }),
    /** Sunucu yapılandırması: iç ağ test alıcıları yalnız üretim dışında ve sabit izin listesiyle */
    testReceiversAllowed: z.boolean(),
});
/** Sır YALNIZ oluşturma / döndürme yanıtında, bir kez döner */
export const webhookSecretResponseSchema = z.object({
    subscription: webhookSubscriptionViewSchema,
    secret: z.string(),
});
export const tenantWebhookSecretResponseSchema = z.object({
    version: z.number().int(),
    secret: z.string(),
    /** Rotasyon geçişi başladıysa önceki sır (sürüm + bitiş); ilk oluşturmada ya da geçişsiz rotasyonda null */
    previous: webhookPreviousSecretSchema.nullable(),
});
/** "Önceki anahtarı şimdi sonlandır" yanıtı (tenant sırrı) */
export const tenantWebhookSecretStateSchema = z.object({
    configured: z.boolean(),
    version: z.number().int().nullable(),
    rotatedAt: isoDate.nullable(),
    previous: webhookPreviousSecretSchema.nullable(),
});
export const WEBHOOK_DELIVERY_STATUSES = [
    'queued',
    'sending',
    'delivered',
    'failed',
    'unknown',
];
export const WEBHOOK_DELIVERY_STATUS_LABELS = {
    queued: 'Kuyrukta',
    sending: 'Gönderiliyor',
    delivered: 'Kabul edildi',
    failed: 'Başarısız',
    unknown: 'Sonucu belirsiz',
};
export const WEBHOOK_ATTEMPT_OUTCOMES = [
    'delivered',
    'retry',
    'failed',
    'blocked',
    'unknown',
];
/** Hata sınıfları (kod → Türkçe açıklama); yanıt gövdesi saklanmaz */
export const WEBHOOK_ERROR_LABELS = {
    http_3xx: 'Alıcı yönlendirme döndürdü; yönlendirme izlenmez',
    http_4xx: 'Alıcı isteği reddetti (4xx)',
    http_5xx: 'Alıcı hata döndürdü (5xx)',
    http_408: 'Alıcı zaman aşımı bildirdi (408)',
    http_429: 'Alıcı istek sınırına takıldı (429)',
    connect_timeout: 'Bağlantı kurulamadı (zaman aşımı)',
    connect_failed: 'Bağlantı kurulamadı',
    tls_failed: 'TLS doğrulaması başarısız',
    response_timeout: 'İstek gönderildi, yanıt gelmedi (sonuç belirsiz)',
    connection_lost: 'İstek gönderildi, bağlantı koptu (sonuç belirsiz)',
    worker_interrupted: 'Teslim sırasında işlem yarıda kaldı (sonuç belirsiz)',
    dns_failed: 'Alan adı çözümlenemedi',
    private_address: 'Adres iç ağa, yerel ağa ya da bulut metadata adresine çıkıyor',
    invalid_host: 'Geçersiz alan adı',
    port_not_allowed: 'Bu port kullanılamaz (yalnız 80/443)',
    subscription_inactive: 'Abonelik pasif',
    signing_secret_missing: 'İmza sırrı tanımlı değil',
    secret_unavailable: 'İmza sırrı çözülemedi (anahtar sağlayıcı); sistem yöneticisine bildirin',
    worker_error: 'Teslim denemesi tekrar tekrar başlatılamadı',
};
export const webhookAttemptViewSchema = z.object({
    attemptNo: z.number().int(),
    startedAt: isoDate,
    finishedAt: isoDate.nullable(),
    outcome: z.enum(WEBHOOK_ATTEMPT_OUTCOMES).nullable(),
    statusCode: z.number().int().nullable(),
    errorCode: z.string().nullable(),
    durationMs: z.number().int().nullable(),
    /**
     * Bu denemenin imzalandığı sır SÜRÜMLERİ (değer değil), başlıktaki `v1` sırasıyla: geçiş süresinde `[yeni, önceki]`, aksi
     * hâlde `[güncel]`. İmzalanmadan kapanan deneme ve M6-H3 öncesi kayıtlar için null.
     */
    signatureVersions: z.array(z.number().int()).nullable(),
});
export const webhookDeliveryViewSchema = z.object({
    id: uuid,
    subscriptionId: uuid.nullable(),
    subscriptionName: z.string().nullable(),
    targetKind: z.enum(['subscription', 'url']),
    url: z.string(),
    eventId: uuid,
    eventType: z.string(),
    source: z.enum(['subscription', 'automation']),
    status: z.enum(WEBHOOK_DELIVERY_STATUSES),
    attemptCount: z.number().int(),
    maxAttempts: z.number().int(),
    nextAttemptAt: isoDate.nullable(),
    lastStatusCode: z.number().int().nullable(),
    lastError: z.string().nullable(),
    deliveredAt: isoDate.nullable(),
    redeliveryOf: uuid.nullable(),
    createdAt: isoDate,
});
export const webhookDeliveryListSchema = z.object({
    deliveries: z.array(webhookDeliveryViewSchema),
    nextCursor: z.string().nullable(),
});
export const webhookDeliveryDetailSchema = z.object({
    delivery: webhookDeliveryViewSchema,
    attempts: z.array(webhookAttemptViewSchema),
    /** Gönderilen gövde (imzalanan baytlar; sır ve imza içermez) */
    body: z.string(),
});
export const webhookDeliveryQuerySchema = z
    .object({
    status: z.enum(WEBHOOK_DELIVERY_STATUSES).optional(),
    /** Başarısız teslimler görünümü: failed + unknown */
    problem: z.enum(['1']).optional(),
    subscriptionId: uuid.optional(),
    cursor: z.string().max(200).optional(),
})
    .strict();
//# sourceMappingURL=integrations.js.map